/**
 * 계좌 통계 카드(AccountStatsCard)의 순수 판정. (2026-09-12)
 *
 * 🔴 왜 컴포넌트에서 떼어냈나
 *   예수금 경고 문턱, "오늘/N일째" 문구, 승률 옆 모수 표기는 특정 값에서만
 *   달라진다. 손으로 그 상태를 만들기 번거로워 눈으로는 검증하기 어렵다.
 *   paperDisplay.ts · bandDisplay.ts 와 같은 이유다.
 *
 * 🔴 비율은 전부 백엔드가 계산해 내려준다 (CLAUDE.md 6.1절)
 *   cashRatioPercent · winRate · maxDrawdownPercent 를 여기서 다시 나누지 않는다.
 *   JS 부동소수 반올림이 Java HALF_UP 과 갈려 화면과 Slack 이 다른 값을 보인다.
 */

import { roundHalfUp } from '@/lib/format';

/**
 * 추천 3계좌와 저점매수 3계좌(저점매수 · 리스크 · 챌린저 · 9/28 I-78 2차)가 공통으로 갖는 계좌 통계.
 *
 * 백엔드 PaperOverviewResponse.Account 와 BandStatusResponse.Account 가
 * 같은 이름·뜻으로 내려주는 필드만 모았다. 저점매수 쪽은 총자산 이름이
 * equity 라 호출부가 total 로 옮겨 담는다 (BandPanel 참조).
 */
export interface AccountStats {
  /** 🔴 이번 회차 시작 금액 - 수익률의 분모. 1~3회차는 시드, 보유를 넘긴 회차부터는 시작일 평가액 (U-31) */
  seed: number;
  cash: number;
  /** 총자산. 예수금 + 보유 평가액 */
  total: number;
  returnPercent: number | null;
  /** 청산 확정 손익 합(수수료·세금 뺀 순액). 회차 시작 이후 청산만 */
  realizedPnl: number;
  /** 보유 평가손익 합 */
  unrealizedPnl: number;
  /** 지금 전량 매도하면 손에 남는 총액. 매도 수수료·거래세를 뺀 값 */
  liquidationValue: number;
  /** 예수금 / 총자산 x 100. 총자산이 0 이면 null */
  cashRatioPercent: number | null;
  /** 회차 시작 이후 고점 대비 최대 낙폭(%). 스냅샷이 없으면 null */
  maxDrawdownPercent: number | null;
  /** 회차 시작 이후 청산 건수 */
  closedCount: number;
  /** 그중 실현손익 > 0 */
  winCount: number;
  /** winCount / closedCount x 100. 청산이 0건이면 null */
  winRate: number | null;
  /** 처음 원금 (2026-10-19). seed(이번 회차 시작)와 다르면 그 차이가 지난 회차까지의 손익이다 */
  principal: number;
  /** 원금 대비 누적 손익(원) = 총자산 - principal */
  cumulativeProfit: number;
  /** 원금 대비 누적 수익률(%). 백엔드 값 그대로 찍는다 */
  cumulativeReturnPercent: number | null;
}

/**
 * 지난 회차까지 쌓인 손익(원) = 이번 회차 시작 금액(seed) − 처음 원금(principal). (2026-10-19 사용자 피드백)
 *
 * 🔴 원 단위로 0 이면 null 이다
 *   원금 그대로 이번 회차를 시작한 계좌(이번 회차에 처음 사기 시작한 계좌 · 1~3회차처럼 회차마다 원금으로 되돌린 때)는 둘이 같다. 그때 식에 "+0원" 항을 넣거나
 *   "이번 회차" 를 따로 적으면 같은 숫자가 두 번 나온다. 화면은 null 이면 그 항 · 그 줄을 그리지 않는다.
 *   seed 는 DB 소수 4자리라(예: 18,360,797.795) 화면에 찍히는 원 단위로 견준다.
 */
export function priorRoundsProfit(stats: Pick<AccountStats, 'seed' | 'principal'>): number | null {
  const prior = stats.seed - stats.principal;
  return roundHalfUp(prior, 0) === 0 ? null : prior;
}

/**
 * 예수금 비중이 이 값(%) 미만이면 "예수금 10% 미만" 배지를 붙인다.
 *
 * 사다리 2·3단이나 추가 매수를 받을 실탄의 기준이다. 저점매수 계좌는
 * 한 종목을 최대 3번 나눠 사는데, 예수금이 총자산의 1할 아래면 다음 단이
 * 왔을 때 살 돈이 없다. 추천 계좌도 다음 추천을 받을 자리가 없다는 뜻이다.
 *
 * 🔴 경고가 아니라 정보다
 *   사용자는 실제 현금을 따로 갖고 있다. 모의 계좌의 예수금이 바닥난 것은
 *   "돈이 없다" 가 아니라 "이 계좌는 지금 풀매수 상태다" 라는 사실 표시다.
 *   그래서 Badge 는 danger 가 아니라 warning 이다.
 */
export const LOW_CASH_RATIO_PERCENT = 10;

/**
 * 예수금 비중이 낮은가. 비중을 모르면(총자산 0) 경고를 붙일 근거가 없다.
 */
export function isLowCash(cashRatioPercent: number | null): boolean {
  if (cashRatioPercent === null || !Number.isFinite(cashRatioPercent)) {
    return false;
  }
  return cashRatioPercent < LOW_CASH_RATIO_PERCENT;
}

/**
 * 보유 기간 문구. 매수 시각 옆에 " · N일째" 로 붙는다.
 *
 * 0일이면 "오늘" 이다. "0일째" 는 말이 안 되고, 오늘 산 것이라는 사실이
 * 숫자보다 잘 읽힌다. 백엔드가 매수일 → 오늘(KST) 일수를 세어 준다.
 */
export function daysHeldLabel(daysHeld: number): string {
  return daysHeld <= 0 ? '오늘' : `${daysHeld}일째`;
}

/**
 * 승률 문구. "승률 82.2% (37/45)" 꼴로, 모수를 반드시 함께 적는다.
 *
 * 🔴 청산이 0건이면 "청산 없음" 이다. "0%" 로 두면 전패로 읽힌다.
 *   (paperDisplay.winRateLabel 과 같은 원칙. 그쪽은 비교 표용 긴 문구라
 *   "37승 8패 · 모수 45건" 이고, 여기는 한 줄 카드용 짧은 꼴이다)
 *
 * winRate 는 백엔드가 scale 1 로 반올림해 준 값이라 toFixed(1) 은
 * 자릿수를 맞추기만 한다 - 다시 계산하지 않는다.
 */
export function winRateText(stats: Pick<AccountStats, 'closedCount' | 'winCount' | 'winRate'>): string {
  if (stats.closedCount === 0 || stats.winRate === null) {
    return '청산 없음';
  }
  return `승률 ${stats.winRate.toFixed(1)}% (${stats.winCount}/${stats.closedCount})`;
}
